/**
 * Loan Affordability Calculator Page
 * Calculate the maximum loan amount you can afford based on your income
 */
import { useState, useEffect } from 'react';
import { useCustomTranslation } from '../../hooks';
import { trackPageView } from '../../utils/analytics';
import { Box, Typography, TextField, Button, Card, CardContent } from '@mui/material';
import AmazonRecommendation from '../../components/common/AmazonRecommendation';


interface LoanAffordabilityData {
  monthly_income: number;
  monthly_expenses: number;
  interest_rate: number;
  loan_term_years: number;
  debt_to_income_ratio: number;
}

interface LoanAffordabilityResults {
  max_monthly_payment: number;
  max_loan_amount: number;
  available_income: number;
  debt_to_income_percentage: number;
}

export default function LoanAffordabilityPage() {
  const { t, i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';
  const [formData, setFormData] = useState<LoanAffordabilityData>({
    monthly_income: 0,
    monthly_expenses: 0,
    interest_rate: 2.5,
    loan_term_years: 30,
    debt_to_income_ratio: 0.4,
  });
  const [results, setResults] = useState<LoanAffordabilityResults | null>(null);

  useEffect(() => {
    trackPageView('/calculators/loan-affordability', 'Loan Affordability Calculator');
    document.title = `${t('calculators.loanAffordability.title')} | PayWise365`;
  }, [t]);

  const handleCalculate = () => {
    const available_income = formData.monthly_income - formData.monthly_expenses;
    const max_monthly_payment = formData.monthly_income * formData.debt_to_income_ratio;
    const monthlyRate = formData.interest_rate / 100 / 12;
    const totalMonths = formData.loan_term_years * 12;
    
    // Calculate max loan amount using reverse mortgage formula
    const max_loan_amount = max_monthly_payment * 
      ((Math.pow(1 + monthlyRate, totalMonths) - 1) / 
       (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)));
    
    const debt_to_income_percentage = (max_monthly_payment / formData.monthly_income) * 100;

    setResults({
      max_monthly_payment,
      max_loan_amount,
      available_income,
      debt_to_income_percentage,
    });
  };

  return (
    <div className="calculator-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>{t('calculators.loanAffordability.title')}</h1>
          <p>{t('calculators.loanAffordability.pageSubtitle')}</p>
        </div>
      </div>
      
      <main className="calculator-content">
        <div className="calculator-container">
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {t('form.title')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <TextField
                  fullWidth
                  label="Monthly Income (HKD)"
                  type="number"
                  value={formData.monthly_income || ''}
                  onChange={(e) => setFormData({ ...formData, monthly_income: parseFloat(e.target.value) || 0 })}
                />
                <TextField
                  fullWidth
                  label="Monthly Expenses (HKD)"
                  type="number"
                  value={formData.monthly_expenses || ''}
                  onChange={(e) => setFormData({ ...formData, monthly_expenses: parseFloat(e.target.value) || 0 })}
                />
                <TextField
                  fullWidth
                  label="Interest Rate (%)"
                  type="number"
                  value={formData.interest_rate}
                  onChange={(e) => setFormData({ ...formData, interest_rate: parseFloat(e.target.value) || 0 })}
                />
                <TextField
                  fullWidth
                  label="Loan Term (Years)"
                  type="number"
                  value={formData.loan_term_years}
                  onChange={(e) => setFormData({ ...formData, loan_term_years: parseInt(e.target.value) || 0 })}
                />
                <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                  <TextField
                    fullWidth
                    label="Debt-to-Income Ratio (0.3 = 30%)"
                    type="number"
                    inputProps={{ step: 0.1, min: 0.1, max: 0.5 }}
                    value={formData.debt_to_income_ratio}
                    onChange={(e) => setFormData({ ...formData, debt_to_income_ratio: parseFloat(e.target.value) || 0 })}
                    helperText="Recommended: 0.3-0.4 (30-40%)"
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                  <Button variant="contained" fullWidth onClick={handleCalculate} size="large">
                    Calculate
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {results && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  Results
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Maximum Monthly Payment</Typography>
                      <Typography variant="h5">HKD {results.max_monthly_payment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'success.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Maximum Loan Amount</Typography>
                      <Typography variant="h5">HKD {results.max_loan_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'info.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Available Income</Typography>
                      <Typography variant="h5">HKD {results.available_income.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'warning.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Debt-to-Income Ratio</Typography>
                      <Typography variant="h5">{results.debt_to_income_percentage.toFixed(1)}%</Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <div style={{ width: '100%', padding: '30px 20px' }}>
        <AmazonRecommendation isEnglish={isEnglish} />
      </div>
    </div>
  );
}
